import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import {
  BADGE_SX,
  SURFACE_SX,
  formatDate,
  formatValue,
} from './customerDetailUtils';

const SUMMARY_ITEM_SX = {
  p: 1.25,
  borderRadius: 3,
  border: '1px solid rgba(0,0,0,0.06)',
  backgroundColor: '#fcfdfd',
  minWidth: 0,
};

function SummaryBlock({ eyebrow, title, body, Icon }) {
  return (
    <Box sx={SUMMARY_ITEM_SX}>
      <Stack spacing={0.85}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,137,123,0.08)',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {eyebrow}
            </Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
      </Stack>
    </Box>
  );
}

const CustomerDetailOverviewCard = memo(function CustomerDetailOverviewCard({
  disabled = false,
  openActivityCount,
  nextActivity,
  primaryContact,
  priorityLabel,
  segmentLabel,
  onAddActivity,
  onAddContact,
}) {
  const openWorkTitle = disabled
    ? 'Open work unavailable'
    : `${openActivityCount} open activit${openActivityCount === 1 ? 'y' : 'ies'}`;
  const openWorkBody = disabled
    ? 'Reconnect Fortnox to load upcoming CRM work for this customer.'
    : nextActivity
      ? `Next due ${formatDate(nextActivity?.dueDate)} • ${formatValue(nextActivity?.title, 'Untitled activity')}`
      : 'No open CRM activity is waiting on this customer.';
  const contactTitle = disabled
    ? 'Primary contact unavailable'
    : primaryContact
      ? primaryContact.name
      : 'No active contact saved';
  const contactBody = disabled
    ? 'Contact details stay blocked until the customer can be resolved again.'
    : primaryContact
      ? formatValue(
          [primaryContact.title, primaryContact.phone || primaryContact.email].filter(Boolean).join(' • '),
          'No contact role or channel saved'
        )
      : 'Add a contact to keep shared customer access points visible.';
  const overlayTitle = disabled
    ? 'CRM overlay unavailable'
    : `${priorityLabel} priority • ${segmentLabel}`;
  const overlayBody = disabled
    ? 'Priority, segment, and internal CRM context stay read-only while blocked.'
    : 'Priority and segment summarize the tenant-local CRM overlay on top of Fortnox master data.';

  return (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.75, md: 2 } }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Box
                component="span"
                sx={{
                  ...BADGE_SX,
                  color: 'primary.main',
                  backgroundColor: 'rgba(0,137,123,0.08)',
                  borderColor: 'rgba(0,137,123,0.16)',
                }}
              >
                Customer summary
              </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Scan next work first, then contacts and CRM overlay context.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              type="button"
              variant="contained"
              size="small"
              disabled={disabled}
              startIcon={<AddRoundedIcon fontSize="small" />}
              onClick={onAddActivity}
              sx={{ textTransform: 'none' }}
            >
              Add activity
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="small"
              disabled={disabled}
              startIcon={<PeopleAltOutlinedIcon fontSize="small" />}
              onClick={onAddContact}
              sx={{ textTransform: 'none' }}
            >
              Add contact
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.25,
          }}
        >
          <SummaryBlock
            eyebrow="Open work"
            title={openWorkTitle}
            body={openWorkBody}
            Icon={CallRoundedIcon}
          />
          <SummaryBlock
            eyebrow="Primary contact"
            title={contactTitle}
            body={contactBody}
            Icon={PeopleAltOutlinedIcon}
          />
          <SummaryBlock
            eyebrow="CRM overlay"
            title={overlayTitle}
            body={overlayBody}
            Icon={TuneRoundedIcon}
          />
        </Box>
      </Stack>
    </Paper>
  );
});

export default CustomerDetailOverviewCard;
